// CRUD for the user's claimed devices, plus the claim Edge Function call.

import { getSupabaseClient } from './supabase';

export interface DeviceRow {
  id: string;
  hardware_id: string;
  name: string;
  claimed_at: string | null;
  last_seen: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface ClaimedDevice {
  id: string;
  name: string;
  claimed_at: string;
}

export const listDevices = async (): Promise<DeviceRow[]> => {
  const supabase = getSupabaseClient();
  if (!supabase) return [];

  const { data, error } = await supabase
    .from('devices')
    .select('id, hardware_id, name, claimed_at, last_seen, revoked_at, created_at')
    .is('revoked_at', null)
    .not('claimed_at', 'is', null)
    .order('claimed_at', { ascending: false });

  if (error) {
    console.error('Failed to list devices:', error.message);
    return [];
  }
  return (data as DeviceRow[]) || [];
};

export const claimDevice = async (
  pairingCode: string,
  name?: string,
): Promise<ClaimedDevice> => {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase is not configured.');

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('You must be signed in to claim a device.');

  const { data, error } = await supabase.functions.invoke('device-claim', {
    body: { pairing_code: pairingCode, name },
  });

  if (error) {
    // supabase-js wraps function errors; surface a cleaner message
    const message = (error as any)?.context?.error || error.message;
    throw new Error(message || 'Claim failed.');
  }

  return (data as { device: ClaimedDevice }).device;
};

export const renameDevice = async (
  deviceId: string,
  name: string,
): Promise<void> => {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase is not configured.');

  const { error } = await supabase
    .from('devices')
    .update({ name })
    .eq('id', deviceId);

  if (error) throw new Error(error.message);
};

export const removeDevice = async (deviceId: string): Promise<void> => {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase is not configured.');

  // Soft-delete: setting revoked_at via the trigger requires service-role
  // OR a column unprotected by the auth-guard trigger. revoked_at IS
  // guarded — devices are removed by row-delete instead, which is fine
  // because the trigger has no protection on DELETE and RLS allows it.
  const { error } = await supabase
    .from('devices')
    .delete()
    .eq('id', deviceId);

  if (error) throw new Error(error.message);
};
